import { AlertCircle, Coins, Receipt, ShieldCheck, Wallet } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCashAllocation, useStrategyAssumptions } from "@/hooks/useStrategy";
import { formatMoney, formatPercent } from "@/lib/utils";

/**
 * Cash Allocation now consumes a SERVER-COMPUTED tax projection. If the engine
 * is blocked (missing intake), we render a CTA — never a guess.
 */
export function CashAllocationPage() {
  const cash = useCashAllocation();
  const assumptions = useStrategyAssumptions();

  const plan = cash.data?.plan;
  const blocked = plan?.isBlocked ?? false;

  const buckets = [
    {
      icon: ShieldCheck,
      label: "Tax reserve",
      pct: plan?.taxSavingsSweepPercent ?? null,
      account: "High-yield savings (separate)",
      annual: plan?.projectedAnnualTaxReserve ?? null,
      notes:
        "Sized to the engine's marginal next-$ rate × projected revenue. Rebalance after each quarter.",
    },
    {
      icon: Coins,
      label: "Solo 401(k) holding",
      pct: plan?.retirementHoldingPercent ?? null,
      account: "Brokerage Solo 401(k)",
      annual: plan?.projectedAnnualSolo401k ?? null,
      notes:
        "Sized to hit your annual employee deferral + employer profit-share target evenly across the year.",
    },
    {
      icon: Wallet,
      label: "Operating buffer",
      pct: plan?.operatingBufferPercent ?? null,
      account: "Business checking",
      annual: null as number | null,
      notes: "3-month runway target. Overflow rolls to T-bills or owner draw.",
    },
    {
      icon: Receipt,
      label: "Owner draw",
      pct: plan?.ownerDrawPercent ?? null,
      account: "Personal",
      annual: null as number | null,
      notes:
        "Whatever's left after tax + retirement + buffer. S-Corp salary is handled through payroll, not this bucket.",
    },
  ];

  const monthly =
    plan?.projectedAnnualRevenue != null ? plan.projectedAnnualRevenue / 12 : null;

  return (
    <>
      <PageHeader
        eyebrow="Strategy / Cash"
        title="Where every dollar goes"
        description="Computed from your real intake by the tax engine — no marginal-rate slider, no guess."
        actions={
          plan && !blocked && plan.marginalTotalRate != null && (
            <Badge tone="info">
              Marginal next-$ rate: {formatPercent(plan.marginalTotalRate)}
            </Badge>
          )
        }
      />

      {blocked && plan && <BlockedCard plan={plan} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Stat
          label="Projected annual revenue"
          value={formatMoney(plan?.projectedAnnualRevenue ?? null)}
          hint={
            assumptions.data?.hourlyRate != null &&
            assumptions.data?.hoursPerWeek != null &&
            plan?.effectiveBillableWeeks != null
              ? `${assumptions.data.hoursPerWeek} hrs/wk × $${assumptions.data.hourlyRate}/hr × ${plan.effectiveBillableWeeks.toFixed(1)} wks${assumptions.data.contractEndDate ? "" : " (open-ended through Dec 31)"}`
              : "Set hourly rate, hours/wk, contract dates in Settings"
          }
          loading={cash.isLoading}
        />
        <Stat
          label="Projected net SE"
          value={formatMoney(plan?.projectedNetSe ?? null)}
          hint={
            assumptions.data?.projectedAnnualOverhead != null
              ? `Less ${formatMoney(assumptions.data.projectedAnnualOverhead)} overhead`
              : ""
          }
          loading={cash.isLoading}
        />
        <Stat
          label="Monthly inflow target"
          value={formatMoney(monthly)}
          hint="Used to size invoices + sweep math"
          loading={cash.isLoading}
        />
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Per-dollar split</CardTitle>
          <CardDescription>
            For every $1 of revenue, where it goes — same math runs on every paystub the email
            ingestion picks up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cash.isLoading && <Skeleton className="h-32 w-full" />}
          {!cash.isLoading && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Bucket</th>
                  <th className="py-2 text-right">% of revenue</th>
                  <th className="py-2 text-right">Annual target</th>
                  <th className="py-2">Account</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr key={b.label} className="border-b border-border/50">
                    <td className="py-2 font-medium">
                      <span className="inline-flex items-center gap-2">
                        <b.icon className="h-4 w-4 text-primary" />
                        {b.label}
                      </span>
                    </td>
                    <td className="py-2 text-right">{formatPercent(b.pct, 1)}</td>
                    <td className="py-2 text-right">
                      {b.annual != null
                        ? formatMoney(b.annual)
                        : plan?.projectedAnnualRevenue != null && b.pct != null
                          ? formatMoney(plan.projectedAnnualRevenue * b.pct)
                          : "—"}
                    </td>
                    <td className="py-2 text-muted-foreground">{b.account}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {buckets.map((b) => (
          <Card key={b.label} id={b.label}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <b.icon className="h-4 w-4 text-primary" />
                {b.label}
              </CardTitle>
              <CardDescription>{b.account}</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{b.notes}</CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function BlockedCard({
  plan,
}: {
  plan: NonNullable<ReturnType<typeof useCashAllocation>["data"]>["plan"];
}) {
  const blockers = plan.missingInputs.filter((m) => m.isBlocking);
  return (
    <Card className="mb-6 border-amber-500/60 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 text-amber-400" />
          Cash plan blocked
        </CardTitle>
        <CardDescription>
          {plan.blockingReason ??
            "Add the missing inputs and we'll recompute against the real engine."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {blockers.map((m) => {
          const target = m.routeHint ? splitRouteHint(m.routeHint) : null;
          return (
            <div
              key={m.fieldKey}
              className="flex items-start justify-between gap-3 border-b border-border/40 pb-2 last:border-b-0"
            >
              <div>
                <div className="font-medium">{m.label}</div>
                <div className="text-xs text-muted-foreground">{m.why}</div>
              </div>
              {target && (
                <Link to={target.path} hash={target.hash}>
                  <Button size="sm" variant="outline">
                    Open
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
        <div className="pt-2 flex gap-2">
          <Link to="/tax-intake">
            <Button size="sm">Tax Intake</Button>
          </Link>
          <Link to="/settings">
            <Button size="sm" variant="outline">
              Settings
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function splitRouteHint(raw: string): { path: string; hash: string | undefined } {
  const idx = raw.indexOf("#");
  if (idx < 0) return { path: raw, hash: undefined };
  return { path: raw.slice(0, idx), hash: raw.slice(idx + 1) };
}

function Stat({
  label,
  value,
  hint,
  loading,
}: {
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-8 w-32" /> : <div className="text-2xl font-semibold">{value}</div>}
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
